/**
 * @module app.core
 */

import { autoinject, TaskQueue, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { LookupService } from 'app/cd/services/lookup-service';
import { MemberService } from 'app/cd/services/member-service';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class memberCodes extends ComponentBase {

  ////****************** Fields and Properties ************************************////
  @bindable({ defaultBindingMode: bindingMode.toView })
  public memberId: string | null = null;

  @bindable({ defaultBindingMode: bindingMode.toView })
  protected aidCodes: CD.AidCode[] = [];

  @bindable({ defaultBindingMode: bindingMode.toView })
  protected conditions: CD.Condition[] = [];

  protected memberPlans: ReadonlyArray<CD.MemberPlan> = [];
  protected sorting = new Sorting();
  protected resultCount: number = 0;
  public pageSize: number = 10;
  protected rememberExpand: boolean = true;
  public showInactiveToggle: boolean = false;


  ////****************** Element References ***************************************////
  protected $table!: EssTableCustomElement<CD.MemberPlan[]>;
  protected $pager!: EssPagerCustomElement;

  ////****************** Lifecycle events (in order of execution) *****************////
  constructor(
    private readonly lookupService: LookupService,
    private readonly memberService: MemberService,
    private readonly queue: TaskQueue,
    ...rest: any[]) {
    super(...rest);

    // Default Sort
    this.sorting.entries = [
      {
        reference: 'eligibility.rank',
        direction: SortDirection.Ascending
      }
    ];
  }

  protected bind(): void {
    if(!this.memberId) { this.logger.warn('Bindable property [member-id] was not set at binding'); }

    this.reload();
    this.loadLookups()
  }

  ////****************** Events ***************************************************////

  ////****************** Functions ************************************************////
  public async reload(): Promise<void> {
    await this.loadMemberPlans();
  }

  protected applySort(event: SortChangeEvent): void {
    const { property, direction } = event.detail;
    const { sorting } = this;
    if(direction === null) {
      sorting.remove(property);
      if(this.sorting.entries.length == 0) {
        this.sorting.entries = [
          {
            reference: 'eligibility.rank',
            direction: SortDirection.Ascending
          }
        ];
      }
    } else {
      sorting.add({
        reference: property,
        direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
      });
    }
    this.reload();
  }

  private restoreExpanded() {
    if(!this.rememberExpand) { return; }
    // we need to give aurelia a chance to bind the goals into the
    // table before we ask the table to expand the rows
    this.queue.queueTask(() => {
      this.$table.restoreExpanded(this.$table.expanded as Array<CD.MemberPlan>);
    });
  }

  ////****************** Service Calls ********************************************////
  @apiCall
  private async loadMemberPlans(): Promise<void> {
    if(!this.memberId) { return; }

    const options: Partial<IQueryLoadOptions> = {
      top: this.pageSize,
      page: this.$pager?.page || 0,
      sorting: this.sorting,
      includeInactive: this.showInactiveToggle
    };

    let includes = [
      'healthPlan',
      'lineOfBusiness'
    ];

    let fields = [
      'healthPlan.id',
      'healthPlan.description',
      'lineOfBusiness.id',
      'lineOfBusiness.description',
      'identifier.primary',
      'isPrimary'
    ];

    try {
      this.state.beginLoad();

      const response = await this.memberService.loadPlansByMember(this.memberId, options, includes, fields);
      this.memberPlans = response.results;
      this.resultCount = response.count || 0;
      this.restoreExpanded();
    }
    catch(error) {
      throw error;
    }
    finally {
      this.state.endLoad();
    }
  }

  @apiCall
  private async loadLookups(): Promise<void> {
    try {
      this.state.beginLoad();

      const options: Partial<IQueryLoadOptions> = {
        includeInactive: false
      };

      const includes: string[] = [
      ];

      const fields: string[] = [
        'id',
        'description'
      ];

      const [aidCodes, conditions] = await Promise.all(
        [
          this.lookupService.loadAidCodes(includes, options, fields),
          this.lookupService.loadConditions(includes, options, fields),
        ]);

      this.aidCodes = aidCodes.results;
      this.conditions = conditions.results;
    }
    catch(error) {
      throw error;
    }
    finally {
      this.state.endLoad();
    }
  }
}
